'use client';

import FaIcon, { faChevronRight } from '@/components/icon/FaIcon/FaIcon';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import { anton } from '@/lib/font';
import { useGlobalLoadingAtom } from '@/state';
import { BadgeStatus } from '@/types';
import type { GfWorkWithMaterials } from '@/types';
import { formatCreatorNames } from '@/utils/formatters';
import { sendWorkNavigationFromTopEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import clsx from 'clsx';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styles from './WorkCardListWithMaterials.module.scss';

const SHADOW_COLORS = ['#F4DDA5', '#BED3CA', '#F6BFBC'];

function WorkCardListWithMaterials({
  works,
  workLinkPrefix = '/works',
  workLinkParam = {},
}: {
  works: GfWorkWithMaterials[];
  workLinkPrefix?: string;
  workLinkParam?: Record<string, string>;
}) {
  const pathname = usePathname();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const session = useSession();

  const buildWorkUrl = (workId: number, materialId?: number): Route => {
    const basePath = `${workLinkPrefix}/${workId}`;
    const params = new URLSearchParams();

    // workLinkParamを追加
    Object.entries(workLinkParam).forEach(([key, value]) => {
      params.set(key, value);
    });

    // 素材IDがある場合はクエリパラメータに追加
    if (materialId) {
      params.set('selectedMaterialId', materialId.toString());
    }

    const queryString = params.toString();
    return (queryString ? `${basePath}?${queryString}` : basePath) as Route;
  };

  const handleLinkClick = (workId: number, workName: string) => {
    startGlobalLoading();

    // GAイベント送信（TOPページからの遷移の場合のみ）
    const isFromTopPage = pathname === '/';
    if (isFromTopPage) {
      const userId = session?.data?.user.id ?? 0;
      sendWorkNavigationFromTopEvent(workId, workName, userId);
    }
  };

  return (
    <div className={styles.cardContainer}>
      {works.map((work, index) => {
        const shadowColor = SHADOW_COLORS[index % SHADOW_COLORS.length];

        return (
          <article
            key={work.id}
            className={styles.cardWrapper}
            style={{ '--card-shadow-color': shadowColor } as React.CSSProperties}
          >
            {work.badgeStatus && (
              <span
                className={clsx(
                  styles.badge,
                  work.badgeStatus === BadgeStatus.new.value ? styles.badgeNew : styles.badgeAdded,
                  work.badgeStatus === BadgeStatus.new.value && anton.className,
                )}
                aria-hidden='true'
              >
                {BadgeStatus[work.badgeStatus].text}
              </span>
            )}
            <div className={styles.cardBody}>
              <Link
                href={buildWorkUrl(work.id)}
                onClick={() => {
                  handleLinkClick(work.id, work.name);
                }}
                className={styles.coverLink}
              >
                <div className={styles.coverImageContainer}>
                  <NextImageView
                    src={work.imagePath}
                    alt={work.name}
                    className={styles.coverImage}
                    fill
                    objectFit='contain'
                    sizes={getImageLayoutSizes({ sp: '30vw', pc: '200px' })}
                  />
                </div>
              </Link>
              <div className={styles.materialsGrid}>
                {work.materials.map((material) => (
                  <Link
                    key={material.id}
                    href={buildWorkUrl(work.id, material.id)}
                    onClick={() => {
                      handleLinkClick(work.id, work.name);
                    }}
                    className={styles.materialItem}
                  >
                    <NextImageView
                      src={material.thumbnailPath}
                      alt={material.name}
                      containerClassName={styles.materialImage}
                      fill
                      objectFit='contain'
                      sizes={getImageLayoutSizes({ sp: '20vw', pc: '90px' })}
                    />
                  </Link>
                ))}
              </div>
            </div>
            <div className={styles.footerBar}>
              <div className={styles.workInfoBox}>
                <p className={styles.workName}>{work.name}</p>
                <p className={styles.creatorName}>{formatCreatorNames(work.creators)}</p>
              </div>
              <span className={styles.footerArrow} aria-hidden='true'>
                <FaIcon icon={faChevronRight} />
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default WorkCardListWithMaterials;
